import { submitLead } from "../../forms/submit-lead";
import React from "react";
import { Calendar, CheckCircle2, Clock, Mail, Phone, XCircle, Zap } from "lucide-react";
import { formatPhoneDisplay, formatPhoneTel } from "../../lib/phone-formatter";
import { cn } from "../../lib/utils";
import { type BaseComponentProps } from "../../types/puck";
import { getWindowJobsiteConfig } from "../../types/runtime-config";
import { useTurnstile } from "../../hooks/use-turnstile";
import { type Data } from "../../forms/form-data";
import { FormEmbed, FormEmbedSkeleton, useSavedContactForm } from "../../forms/form-embed";
import { SmartLink } from "../smart-link";
import { Alert, AlertDescription } from "../ui/alert";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

export type FormTypePreset = "quick-quote" | "schedule" | "emergency" | "contact";
export type UrgencyLevel = "emergency" | "this-week" | "flexible";
export type ContactFormVariant = "card" | "minimal" | "highlight";

interface FormFieldConfig {
  name: boolean;
  email: boolean;
  phone: boolean;
  message: boolean;
  service: boolean;
  address: boolean;
  dateTime: boolean;
  requiredFields: string[];
}

interface ContactHighlight {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  href?: string;
}

const HOURS_HIGHLIGHT_LABELS = new Set(["Hours", "Emergency Hours"]);

const FORM_PRESETS: Record<FormTypePreset, FormFieldConfig> = {
  "quick-quote": {
    name: true,
    email: false,
    phone: true,
    message: true,
    service: true,
    address: false,
    dateTime: false,
    requiredFields: ["name", "phone"],
  },
  schedule: {
    name: true,
    email: true,
    phone: true,
    message: false,
    service: true,
    address: false,
    dateTime: true,
    requiredFields: ["name", "phone", "email"],
  },
  emergency: {
    name: true,
    email: false,
    phone: true,
    message: false,
    service: false,
    address: true,
    dateTime: false,
    requiredFields: ["name", "phone", "address"],
  },
  contact: {
    name: true,
    email: true,
    phone: true,
    message: true,
    service: false,
    address: false,
    dateTime: false,
    requiredFields: ["name", "email"],
  },
};

export interface ContactFormProps extends BaseComponentProps {
  heading: string;
  subheading?: string;
  formType?: FormTypePreset;
  variant?: ContactFormVariant;
  showUrgencySelector?: "yes" | "no" | boolean;
  submitButtonText?: string;
  successMessage?: string;
  backgroundColor?: string;
  layout?: "centered" | "split";
  serviceOptions?: string[];
  schemaEnabled?: boolean;
  businessName?: string;
  businessPhone?: string;
  businessEmail?: string;
  businessUrl?: string;
  hasTrackingPhone?: boolean;
  availableLanguage?: string;
  hoursAvailable?: string;
  /** Canonical slug for photo-upload URLs (same slug the booking widget uses). */
  businessSlug?: string;
  /** Dashboard preview / builder canvas: render this draft custom form (no
   *  network, no submit). `renderFields` swaps the field area for an editable
   *  Puck canvas; `panelOnly` drops the page chrome (heading/section — those
   *  belong to the jobsite page instance) and renders just the form panel.
   *  Live surfaces never set this. Mirrors BookingWidgetInline.preview. */
  preview?: {
    formData: Data;
    renderFields?: () => React.ReactNode;
    panelOnly?: boolean;
  };
}

const generateContactPointSchema = (props: {
  businessName?: string;
  businessPhone?: string;
  businessEmail?: string;
  businessUrl?: string;
  availableLanguage?: string;
  hoursAvailable?: string;
  formType?: FormTypePreset;
}): Record<string, unknown> | null => {
  const {
    businessName,
    businessPhone,
    businessEmail,
    businessUrl,
    availableLanguage,
    hoursAvailable,
    formType,
  } = props;

  if (!businessPhone && !businessEmail) return null;

  const contactType =
    formType === "emergency"
      ? "emergency"
      : formType === "schedule"
        ? "reservations"
        : "customer service";

  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "ContactPoint",
    contactType,
  };

  if (businessPhone) schema.telephone = businessPhone;
  if (businessEmail) schema.email = businessEmail;
  if (businessUrl) schema.url = businessUrl;
  if (availableLanguage) schema.availableLanguage = availableLanguage;
  if (hoursAvailable) schema.hoursAvailable = hoursAvailable;
  if (businessName) {
    schema.areaServed = {
      "@type": "Country",
      name: "US",
    };
  }

  return schema;
};

function buildContactHighlights(props: {
  businessPhone?: string;
  businessEmail?: string;
  hoursAvailable?: string;
  formType: FormTypePreset;
}): ContactHighlight[] {
  const highlights: ContactHighlight[] = [];

  if (props.businessPhone) {
    highlights.push({
      icon: Phone,
      label: "Call",
      value: formatPhoneDisplay(props.businessPhone),
      href: `tel:${formatPhoneTel(props.businessPhone)}`,
    });
  }

  if (props.businessEmail) {
    highlights.push({
      icon: Mail,
      label: "Email",
      value: props.businessEmail,
      href: `mailto:${props.businessEmail}`,
    });
  }

  if (props.hoursAvailable) {
    highlights.push({
      icon: Clock,
      label: props.formType === "emergency" ? "Emergency Hours" : "Hours",
      value: props.hoursAvailable,
    });
  }

  return highlights;
}

export const ContactForm: React.FC<ContactFormProps> = ({
  id,
  heading,
  subheading,
  formType = "contact",
  variant = "card",
  showUrgencySelector = "no",
  submitButtonText,
  successMessage = "Thank you! We'll be in touch soon.",
  backgroundColor,
  layout = "centered",
  serviceOptions = [],
  schemaEnabled = true,
  businessName,
  businessPhone,
  businessEmail,
  businessUrl,
  hasTrackingPhone,
  availableLanguage = "en",
  hoursAvailable,
  businessSlug,
  preview,
  className,
}) => {
  const panelOnly = preview?.panelOnly === true;
  const schema = schemaEnabled
    ? generateContactPointSchema({
        businessName,
        businessPhone,
        businessEmail,
        businessUrl,
        availableLanguage,
        hoursAvailable,
        formType,
      })
    : null;
  const [formState, setFormState] = React.useState<"idle" | "submitting" | "success" | "error">(
    "idle",
  );
  const [formData, setFormData] = React.useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    service: "",
    address: "",
    preferredDate: "",
    preferredTime: "",
    urgency: "" as UrgencyLevel | "",
  });
  const fieldConfig = FORM_PRESETS[formType];

  // A saved custom form REPLACES the preset field list — but only for the
  // "contact" preset (quick-quote/schedule/emergency stay preset-driven), and
  // only when the business has one (null → preset markup, unchanged). While
  // the read is in flight a skeleton holds the spot, so a saved form never
  // flashes the preset first. See wiki/decisions/form-builder-contact-tasks.md.
  const saved = useSavedContactForm(!preview && formType === "contact");
  const customForm = preview?.formData ?? (formType === "contact" ? saved.data : null);
  const resolvingForm = !preview && formType === "contact" && saved.loading;
  const contactHighlights = buildContactHighlights({
    businessPhone,
    businessEmail,
    hoursAvailable,
    formType,
  });

  const shouldShowUrgency = () => {
    if (typeof showUrgencySelector === "boolean") return showUrgencySelector;
    return showUrgencySelector === "yes";
  };

  const getDefaultSubmitText = () => {
    if (submitButtonText) return submitButtonText;

    switch (formType) {
      case "quick-quote":
        return "Get Free Quote";
      case "schedule":
        return "Schedule Service";
      case "emergency":
        return "Request Emergency Service";
      default:
        return "Send Message";
    }
  };

  const isRequired = (fieldName: string) => fieldConfig.requiredFields.includes(fieldName);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleUrgencySelect = (level: UrgencyLevel) => {
    setFormData((prev) => ({ ...prev, urgency: level }));
  };

  const {
    tokenRef: turnstileTokenRef,
    containerRef: turnstileContainerRef,
    containerClassName: turnstileClassName,
  } = useTurnstile();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    if (!formEl.checkValidity()) {
      formEl.reportValidity();
      return;
    }

    const config = getWindowJobsiteConfig();
    const turnstileToken = turnstileTokenRef.current;
    if (config?.turnstileSiteKey && !turnstileToken) {
      setFormState("error");
      return;
    }

    setFormState("submitting");

    try {
      if (config?.apiUrl && config?.siteId) {
        const params = new URLSearchParams(window.location.search);
        const isPreview = params.get("preview") === "true";
        const sessionId = isPreview ? null : sessionStorage.getItem("sp_session_id") || null;

        const result = await submitLead({
          apiUrl: config.apiUrl,
          siteId: config.siteId,
          backupApiUrl: config.backupApiUrl,
          businessName: config.business?.name,
          payload: {
            ...formData,
            formType,
            variant: config?.variant,
            sessionId,
            ...(turnstileToken ? { turnstileToken } : {}),
          },
        });
        if (result.status === "failed") {
          throw new Error("Lead submission failed");
        }
      }

      setFormState("success");
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
        service: "",
        address: "",
        preferredDate: "",
        preferredTime: "",
        urgency: "",
      });
      window.setTimeout(() => setFormState("idle"), 5000);
    } catch {
      setFormState("error");
      window.setTimeout(() => setFormState("idle"), 3000);
    }
  };

  const formPanelClass = cn(
    "jobsite:p-6 jobsite:sm:p-8",
    variant === "card" &&
      "jobsite:border jobsite:border-border/80 jobsite:bg-background jobsite:shadow-xl",
    variant === "minimal" && "jobsite:border jobsite:border-border/70 jobsite:bg-background",
    variant === "highlight" &&
      "jobsite:border jobsite:border-primary/10 jobsite:bg-background jobsite:shadow-2xl",
  );
  const selectFieldClass =
    "jobsite:h-12 jobsite:w-full jobsite:border jobsite:border-input jobsite:bg-transparent jobsite:px-3 jobsite:text-base jobsite:text-foreground jobsite:transition-[color,box-shadow] jobsite:outline-none jobsite:focus-visible:border-ring jobsite:focus-visible:ring-ring/50 jobsite:focus-visible:ring-[3px] jobsite:disabled:pointer-events-none jobsite:disabled:cursor-not-allowed jobsite:disabled:opacity-50";
  const sectionClass = cn(
    "jobsite:py-16 jobsite:md:py-24",
    backgroundColor
      ? ""
      : variant === "highlight"
        ? "jobsite:bg-muted/25"
        : "jobsite:bg-background",
    className,
  );
  const urgencyButtonClass = (value: UrgencyLevel) =>
    cn(
      "jobsite:flex jobsite:flex-col jobsite:items-center jobsite:gap-1 jobsite:border jobsite:p-3 jobsite:transition-colors",
      formData.urgency === value
        ? value === "emergency"
          ? "jobsite:border-destructive jobsite:bg-destructive/10 jobsite:text-destructive"
          : "jobsite:border-primary jobsite:bg-primary/10 jobsite:text-primary"
        : "jobsite:border-border jobsite:hover:border-primary/40",
    );
  const surfaceRadiusStyle = { borderRadius: "var(--jobsite-radius)" } as const;

  const renderHighlights = () => {
    if (contactHighlights.length === 0) return null;

    return (
      <div className="jobsite:grid jobsite:gap-3 jobsite:sm:grid-cols-2">
        {contactHighlights.map((item) => {
          const Icon = item.icon;
          const isHoursHighlight =
            HOURS_HIGHLIGHT_LABELS.has(item.label) || item.value.includes("\n");
          const content = (
            <div className="jobsite:flex jobsite:min-w-0 jobsite:items-start jobsite:gap-3">
              <div
                className="jobsite:flex jobsite:h-10 jobsite:w-10 jobsite:shrink-0 jobsite:items-center jobsite:justify-center jobsite:bg-primary/10 jobsite:text-primary"
                style={surfaceRadiusStyle}
              >
                <Icon className="jobsite:h-4 jobsite:w-4" />
              </div>
              <div className="jobsite:min-w-0">
                <p className="jobsite:text-xs jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.18em] jobsite:text-primary/60">
                  {item.label}
                </p>
                <p
                  className={cn(
                    "jobsite:mt-1 jobsite:text-sm jobsite:font-medium jobsite:text-foreground jobsite:[overflow-wrap:anywhere]",
                    isHoursHighlight
                      ? "jobsite:whitespace-pre-line jobsite:leading-relaxed"
                      : "jobsite:leading-snug",
                  )}
                >
                  {item.value}
                </p>
              </div>
            </div>
          );

          return item.href ? (
            <SmartLink
              key={`${item.label}-${item.value}`}
              to={item.href}
              className={cn(
                "jobsite:min-w-0 jobsite:border jobsite:border-border/70 jobsite:bg-background jobsite:p-4 jobsite:transition-colors jobsite:hover:border-primary/30",
                isHoursHighlight && "jobsite:sm:col-span-2",
              )}
              style={surfaceRadiusStyle}
            >
              {content}
            </SmartLink>
          ) : (
            <div
              key={`${item.label}-${item.value}`}
              className={cn(
                "jobsite:min-w-0 jobsite:border jobsite:border-border/70 jobsite:bg-background jobsite:p-4",
                isHoursHighlight && "jobsite:sm:col-span-2",
              )}
              style={surfaceRadiusStyle}
            >
              {content}
            </div>
          );
        })}
      </div>
    );
  };

  // A saved custom form renders through the shared FormEmbed (the one machine
  // every surface uses); absent form = the preset markup below, unchanged.
  const customFormEmbed = customForm ? (
    <FormEmbed
      data={customForm}
      renderFields={preview?.renderFields}
      disableSubmit={!!preview}
      submitLabel={getDefaultSubmitText()}
      successMessage={successMessage}
      formType={formType}
      hasTrackingPhone={hasTrackingPhone}
      businessSlug={businessSlug}
      buttonClassName={cn(
        variant === "highlight" && "jobsite:rounded-full",
        formType === "emergency" && "jobsite:bg-destructive jobsite:hover:bg-destructive/90",
      )}
    />
  ) : null;

  const presetFormContent = (
    <form onSubmit={handleSubmit} className="jobsite:space-y-4">
      <div className="jobsite:grid jobsite:gap-4">
        {fieldConfig.name && (
          <Input
            type="text"
            name="name"
            autoComplete="name"
            placeholder={`Your Name${isRequired("name") ? " *" : ""}`}
            required={isRequired("name")}
            value={formData.name}
            onChange={handleChange}
            disabled={formState === "submitting"}
            className="jobsite:h-12 jobsite:border-2 jobsite:text-base"
          />
        )}

        {fieldConfig.email && (
          <Input
            type="email"
            name="email"
            autoComplete="email"
            placeholder={`Email Address${isRequired("email") ? " *" : ""}`}
            required={isRequired("email")}
            value={formData.email}
            onChange={handleChange}
            disabled={formState === "submitting"}
            className="jobsite:h-12 jobsite:border-2 jobsite:text-base"
          />
        )}

        {fieldConfig.phone && (
          <Input
            type="tel"
            name="phone"
            autoComplete="tel"
            inputMode="tel"
            placeholder={`Phone Number${isRequired("phone") ? " *" : ""}`}
            required={isRequired("phone")}
            value={formData.phone}
            onChange={handleChange}
            disabled={formState === "submitting"}
            className="jobsite:h-12 jobsite:border-2 jobsite:text-base"
          />
        )}

        {fieldConfig.service && serviceOptions.length > 0 && (
          <select
            name="service"
            value={formData.service}
            onChange={(e) => setFormData((prev) => ({ ...prev, service: e.target.value }))}
            disabled={formState === "submitting"}
            className={selectFieldClass}
            style={surfaceRadiusStyle}
          >
            <option value="">Select a Service</option>
            {serviceOptions.map((service) => (
              <option key={service} value={service}>
                {service}
              </option>
            ))}
          </select>
        )}

        {fieldConfig.address && (
          <Input
            type="text"
            name="address"
            autoComplete="street-address"
            placeholder={`Service Address${isRequired("address") ? " *" : ""}`}
            required={isRequired("address")}
            value={formData.address}
            onChange={handleChange}
            disabled={formState === "submitting"}
            className="jobsite:h-12 jobsite:border-2 jobsite:text-base"
          />
        )}

        {fieldConfig.dateTime && (
          <div className="jobsite:grid jobsite:gap-3 jobsite:sm:grid-cols-2">
            <Input
              type="date"
              name="preferredDate"
              value={formData.preferredDate}
              onChange={handleChange}
              disabled={formState === "submitting"}
              className="jobsite:h-12 jobsite:border-2 jobsite:text-base"
            />
            <Input
              type="time"
              name="preferredTime"
              value={formData.preferredTime}
              onChange={handleChange}
              disabled={formState === "submitting"}
              className="jobsite:h-12 jobsite:border-2 jobsite:text-base"
            />
          </div>
        )}

        {shouldShowUrgency() && (
          <div className="jobsite:space-y-2">
            <label className="jobsite:text-sm jobsite:font-medium jobsite:text-muted-foreground">
              How soon do you need service?
            </label>
            <div className="jobsite:grid jobsite:grid-cols-3 jobsite:gap-2">
              <button
                type="button"
                onClick={() => handleUrgencySelect("emergency")}
                disabled={formState === "submitting"}
                className={urgencyButtonClass("emergency")}
                style={surfaceRadiusStyle}
              >
                <Zap className="jobsite:h-5 jobsite:w-5" />
                <span className="jobsite:text-xs jobsite:font-medium">Today</span>
              </button>
              <button
                type="button"
                onClick={() => handleUrgencySelect("this-week")}
                disabled={formState === "submitting"}
                className={urgencyButtonClass("this-week")}
                style={surfaceRadiusStyle}
              >
                <Calendar className="jobsite:h-5 jobsite:w-5" />
                <span className="jobsite:text-xs jobsite:font-medium">This Week</span>
              </button>
              <button
                type="button"
                onClick={() => handleUrgencySelect("flexible")}
                disabled={formState === "submitting"}
                className={urgencyButtonClass("flexible")}
                style={surfaceRadiusStyle}
              >
                <Clock className="jobsite:h-5 jobsite:w-5" />
                <span className="jobsite:text-xs jobsite:font-medium">Flexible</span>
              </button>
            </div>
          </div>
        )}

        {fieldConfig.message && (
          <Textarea
            name="message"
            placeholder={formType === "quick-quote" ? "Describe your project..." : "Your Message"}
            required={isRequired("message")}
            rows={4}
            value={formData.message}
            onChange={handleChange}
            disabled={formState === "submitting"}
            className="jobsite:resize-none jobsite:border-2 jobsite:text-base"
          />
        )}
      </div>

      {/* Turnstile widget container — CF JS API renders into this div after mount */}
      <div ref={turnstileContainerRef} className={turnstileClassName} />

      <Button
        type="submit"
        disabled={formState === "submitting"}
        className={cn(
          "jobsite:h-12 jobsite:w-full jobsite:font-semibold",
          variant === "highlight" && "jobsite:rounded-full",
          formType === "emergency" && "jobsite:bg-destructive jobsite:hover:bg-destructive/90",
        )}
        size="lg"
      >
        {formState === "submitting" ? "Sending..." : getDefaultSubmitText()}
      </Button>

      {fieldConfig.phone && hasTrackingPhone && (
        <p className="jobsite:text-[11px] jobsite:leading-relaxed jobsite:text-muted-foreground/70">
          By providing your phone number, you consent to receive calls and text messages (SMS). You
          can unsubscribe at any time by replying STOP. Message and data rates may apply.
        </p>
      )}

      {formState === "success" && (
        <Alert className="jobsite:rounded-2xl jobsite:border-emerald-200 jobsite:bg-emerald-50">
          <CheckCircle2 className="jobsite:h-5 jobsite:w-5 jobsite:text-emerald-600" />
          <AlertDescription className="jobsite:text-base jobsite:font-medium jobsite:text-emerald-700">
            {successMessage}
          </AlertDescription>
        </Alert>
      )}

      {formState === "error" && (
        <Alert className="jobsite:rounded-2xl jobsite:border-destructive/20 jobsite:bg-destructive/10">
          <XCircle className="jobsite:h-5 jobsite:w-5 jobsite:text-destructive" />
          <AlertDescription className="jobsite:text-base jobsite:font-medium jobsite:text-destructive">
            Something went wrong. Please try again.
          </AlertDescription>
        </Alert>
      )}
    </form>
  );

  const formContent = resolvingForm ? (
    <FormEmbedSkeleton />
  ) : (
    (customFormEmbed ?? presetFormContent)
  );

  if (panelOnly) {
    return <Card className={formPanelClass}>{formContent}</Card>;
  }

  if (layout === "split") {
    return (
      <section
        id={id || "contact"}
        className={sectionClass}
        style={{ backgroundColor: backgroundColor || undefined }}
      >
        {schema && (
          <script type="application/ld+json">{JSON.stringify(schema)}</script>
        )}
        <div className="jobsite:container jobsite:mx-auto jobsite:px-4">
          <div className="jobsite:grid jobsite:gap-10 jobsite:lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.95fr)] jobsite:lg:items-start">
            <div className="jobsite:space-y-5">
              <div className="jobsite:space-y-4">
                <h2 className="jobsite:font-heading jobsite:text-4xl jobsite:text-foreground jobsite:md:text-5xl">
                  {heading}
                </h2>
                {subheading && (
                  <p className="jobsite:max-w-2xl jobsite:text-lg jobsite:text-muted-foreground jobsite:md:text-xl">
                    {subheading}
                  </p>
                )}
              </div>

              {(variant === "highlight" || contactHighlights.length > 0) && renderHighlights()}
            </div>

            <Card className={formPanelClass}>{formContent}</Card>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id={id || "contact"}
      className={sectionClass}
      style={{ backgroundColor: backgroundColor || undefined }}
    >
      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
      <div className="jobsite:container jobsite:mx-auto jobsite:px-4">
        <div className="jobsite:mx-auto jobsite:max-w-2xl">
          <div className="jobsite:mb-8 jobsite:text-center">
            <h2 className="jobsite:font-heading jobsite:text-4xl jobsite:text-foreground jobsite:md:text-5xl">
              {heading}
            </h2>
            {subheading && (
              <p className="jobsite:mt-4 jobsite:text-lg jobsite:text-muted-foreground jobsite:md:text-xl">
                {subheading}
              </p>
            )}
          </div>

          {variant === "highlight" && contactHighlights.length > 0 && (
            <div className="jobsite:mb-6">{renderHighlights()}</div>
          )}

          <Card className={formPanelClass}>{formContent}</Card>
        </div>
      </div>
    </section>
  );
};
